/**
 * Utility functions for detecting URL patterns to determine appropriate chat CTAs
 */
import { normalizeLanguage, translate } from "./i18n";

/**
 * Normalizes a URL by converting to lowercase and removing trailing slashes and query parameters
 * @param {string} url - The URL to normalize
 * @returns {string} - The normalized URL
 */
export const normalizeUrl = (url) => {
  if (!url) return '';
  
  try {
    // Create a URL object to handle parsing
    let urlObj;
    try {
      // Try to parse as absolute URL
      urlObj = new URL(url);
    } catch (e) {
      // If that fails, try as relative URL
      urlObj = new URL(url, window.location.origin);
    }
    
    // Get the pathname (this automatically handles parsing)
    let normalized = urlObj.pathname;
    
    // Remove trailing slashes
    normalized = normalized.replace(/\/+$/, '');
    
    // Convert to lowercase for case-insensitive matching
    normalized = normalized.toLowerCase();
    
    return normalized;
  } catch (e) {
    // Fallback to basic string processing if URL parsing fails
    console.warn('URL parsing failed, using basic normalization', e);
    
    // Remove protocol (http://, https://)
    let normalized = url.replace(/^(https?:\/\/)/, '');
    
    // Remove domain if present (for relative URLs this won't change anything)
    normalized = normalized.replace(/^[^/]+/, '');
    
    // Remove query parameters
    normalized = normalized.split('?')[0];
    
    // Remove hash fragments
    normalized = normalized.split('#')[0];
    
    // Remove trailing slashes
    normalized = normalized.replace(/\/+$/, '');
    
    // Convert to lowercase for case-insensitive matching
    normalized = normalized.toLowerCase();
    
    return normalized;
  }
};

/**
 * Returns the path segments from a URL
 * @param {string} url - The URL to extract path segments from
 * @returns {Array} - Array of path segments
 */
export const getPathSegments = (url) => {
  const normalized = normalizeUrl(url);
  return normalized.split('/').filter(segment => segment.length > 0);
};

/**
 * Detects if the current URL is a Vehicle Detail Page (VDP)
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a VDP
 */
export const isVehicleDetailPage = (url) => {
  // Clean the URL by removing query parameters before normalizing
  const cleanUrl = url.split('?')[0];
  const normalized = normalizeUrl(cleanUrl);
  const segments = getPathSegments(cleanUrl);
  
  // Extract the full path for more comprehensive checks
  const fullPath = url.toLowerCase();
  
  // Special case for localhost proxy URLs (like http://localhost:3000/https://www.carpoint.it/...)
  let effectivePath = normalized;
  if (fullPath.includes('localhost') && fullPath.includes('https://')) {
    // Extract the actual dealer URL from the localhost proxy
    const dealerUrlMatch = fullPath.match(/localhost:\d+\/(https?:\/\/[^/]+\/.*)/i);
    if (dealerUrlMatch && dealerUrlMatch[1]) {
      effectivePath = dealerUrlMatch[1];
    }
  }
  
  // First check: Look for a specific pattern with numeric ID at the end
  // This handles cases like caddy-cargo-maxi-20-tdi-scr-102cv-business-4530823
  const lastPathSegmentMatch = effectivePath.match(/\/([^/?]+)\/?$/);
  if (lastPathSegmentMatch) {
    const lastSegment = lastPathSegmentMatch[1];
    // Check if the last segment contains a model description followed by numbers
    if (/[a-z-]+-\d+$/.test(lastSegment)) {
      return true;
    }
  }
  
  // Check all segments for vehicle model with ID pattern
  for (const segment of segments) {
    // Look for patterns like "model-name-12345" which is common for vehicle detail pages
    if (/^[a-z0-9-]+-\d{4,}$/i.test(segment)) {
      // Additional verification: check if URL contains vehicle-related keywords
      const vehicleKeywords = ['auto', 'veicol', 'car', 'usate', 'nuove', 'nuovo', 'usato', 'caddy'];
      const containsVehicleKeyword = vehicleKeywords.some(keyword => 
        fullPath.includes(keyword.toLowerCase())
      );
      
      if (containsVehicleKeyword) {
        return true;
      }
    }
  }
  
  // Check for numeric ID at the end of URL path (most reliable indicator)
  // Pattern: any URL ending with a numeric ID like /some-path-12345/
  const numericIdAtEnd = /\/[^/]*\d+\/?$/.test(normalized);
  
  // Extract the last segment to check if it contains a numeric ID
  const lastSegment = segments[segments.length - 1] || '';
  const hasNumericId = /\d+/.test(lastSegment);
  
  if (numericIdAtEnd && hasNumericId) {
    // Additional check: if the URL contains specific vehicle-related keywords
    const vehicleKeywords = ['auto', 'veicol', 'car', 'usate', 'nuove', 'nuovo', 'usato'];
    const containsVehicleKeyword = vehicleKeywords.some(keyword => effectivePath.includes(keyword));
    
    if (containsVehicleKeyword) {
      return true;
    }
  }
  
  // Common VDP patterns as fallback - check against the full URL path
  const vdpPatterns = [
    // Pattern matching the example URL and variations with query parameters
    /\/auto-usate-[^/]+\/[^/]+\/[^/]+\/[^/?]+\/[^/?]+-\d+/,  // /auto-usate-roma/nuove/volkswagen/caddy/caddy-cargo-maxi-20-tdi-scr-102cv-business-4530823/
    /\/[^/]+\/[^/]+\/[^/]+\/[^/]+-\d+/,  // More generic pattern for /path/path/path/model-12345
    /\/caddy-cargo-maxi-[^/]+-\d+/,  // Specific pattern for the example
    // Common Italian patterns
    /\/veicoli?\/[^/]+\/[^/]+\/\d+/,  // /veicoli/brand/model/12345
    /\/auto\/[^/]+\/[^/]+\/\d+/,      // /auto/brand/model/12345
    /\/cars?\/[^/]+\/[^/]+\/\d+/,     // /car/brand/model/12345
    /\/scheda[_-]veicolo\/\d+/,         // /scheda-veicolo/12345
    /\/dettaglio[_-]auto\/\d+/,         // /dettaglio-auto/12345
    /\/vehicle[_-]details?\/\d+/,       // /vehicle-detail/12345
    // Specific patterns for different dealership websites
    /\/usato\/[^/]+\/\d+/,             // /usato/model/12345
    /\/nuovo\/[^/]+\/\d+/,             // /nuovo/model/12345
    /\/stock\/\d+/,                     // /stock/12345
    /\/veicolo-\d+/,                    // /veicolo-12345
    /\/auto-\d+/,                       // /auto-12345
    /\/car-\d+/,                        // /car-12345
  ];
  
  // Check against known patterns using the full path to catch URLs with query parameters
  const matchesPattern = vdpPatterns.some(pattern => pattern.test(fullPath));
  if (matchesPattern) {
    return true;
  }
  
  return false;
};

/**
 * Fixed flutter CTA by URL (strict path segments — avoids e.g. "promozioni-usate" matching promozioni or usate).
 * Priority: promozioni segment → officina/service in slug → exact usate → exact nuove.
 * @param {string} pathname — window.location.pathname
 * @returns {'url-promozioni'|'url-officina'|'url-stock-usate'|'url-stock-nuove'|null}
 */
export function getFixedUrlChatCtaKind(pathname) {
  if (typeof pathname !== 'string' || !pathname) return null;
  const raw = pathname.split('?')[0].split('#')[0];
  const segments = raw.toLowerCase().split('/').filter(Boolean);

  if (segments.includes('promozioni')) return 'url-promozioni';

  const hasOfficina = segments.some((s) => s.includes('officina'));
  const hasService = segments.some((s) => s.includes('service'));
  if (hasOfficina || hasService) return 'url-officina';

  if (segments.includes('usate')) return 'url-stock-usate';
  if (segments.includes('nuove')) return 'url-stock-nuove';

  return null;
}

/**
 * Determines the CTA type based on the current URL
 * @param {string} url - The current URL
 * @returns {string} - The CTA type to display
 */
export const getCTATypeFromUrl = (url) => {
  const normalized = normalizeUrl(url);
  
  // Array of patterns for each CTA type
  const patterns = {
    CAR_SEARCH: ['ricerca', 'gamma', 'modello', 'auto', 'veicoli'],
    PROMOTIONS: ['promozioni', 'offerte', 'sconti', 'promo'],
    SERVICE: ['servizi-officina', 'assistenza', 'manutenzione', 'officina', 'riparazione', 'servizi'],
    RENTAL: ['noleggio', 'rent', 'affitto-auto', 'affitto']
  };
  
  // Check each pattern group
  for (const [ctaType, patternList] of Object.entries(patterns)) {
    for (const pattern of patternList) {
      if (normalized.includes(pattern)) {
        return ctaType;
      }
    }
  }
  
  // If the path has only a single slash, it's the homepage
  if (normalized === '' || normalized === '/') {
    return 'DEFAULT';
  }
  
  return 'DEFAULT';
};

/**
 * Gets the appropriate CTA text and icon based on the CTA type
 * @param {string} ctaType - The CTA type
 * @returns {Object} - Object containing cta text and icon info
 */
export const getCTAConfig = (ctaType, language = "it") => {
  const lang = normalizeLanguage(language);
  switch (ctaType) {
    case 'CAR_SEARCH':
      return {
        text: translate('ctaCarSearch', lang),
        icon: 'search',
        searchComponent: 'MobileCarSearch'
      };
    case 'PROMOTIONS':
      return {
        text: translate('ctaPromotions', lang),
        icon: 'tag',
        searchComponent: 'PromotionsSearch'
      };
    case 'SERVICE':
      return {
        text: translate('ctaService', lang),
        icon: 'tool',
        searchComponent: 'ServiceSearch'
      };
    case 'RENTAL':
      return {
        text: translate('ctaRental', lang),
        icon: 'calendar',
        searchComponent: 'RentalSearch'
      };
    case 'DEFAULT':
    default:
      return {
        text: translate('ctaDefault', lang),
        icon: 'message-circle',
        searchComponent: 'DefaultChat'
      };
  }
};
